/**
 * Coordinates auto-saving a brand-new SOAP note as a draft so files can be
 * attached without a manual "save first" step. It guarantees two invariants
 * that are PHI-critical and easy to regress, so they live here — pure and
 * framework-free — and are unit-tested ([[draftCoordinator.test.ts]]) rather
 * than buried as a closure inside the (huge) SoapNoteDialog component:
 *
 *  1. **Dedupe** — concurrent `ensure()` calls (e.g. several files dropped at
 *     once) share ONE `persist` call; never two drafts for the same note.
 *  2. **Epoch guard** — if the dialog was reopened for a DIFFERENT new note
 *     while a persist was in flight (the epoch moved), the result is discarded
 *     (resolves `undefined`) so one note's id can never stamp onto another note
 *     (a cross-note PHI attach).
 */
export interface DraftCoordinator {
  /** Drop in-flight tracking — call when the dialog reopens for a new note. */
  reset(): void;
  /**
   * @param persist  saves the draft and resolves its id (rejects on failure)
   * @param getEpoch reads the current epoch (bumped on reopen-for-new-note)
   * @returns the new id, or `undefined` if it was discarded (epoch moved)
   */
  ensure(
    persist: () => Promise<string>,
    getEpoch: () => number,
  ): Promise<string | undefined>;
}

export function createDraftCoordinator(): DraftCoordinator {
  let inflight: Promise<string | undefined> | null = null;

  return {
    reset() {
      inflight = null;
    },

    ensure(persist, getEpoch) {
      if (inflight) return inflight;
      const epoch = getEpoch();
      const work = (async () => {
        const id = await persist();
        // Reopened for another new note mid-flight → discard the id so it can't
        // be stamped onto the note now showing.
        return getEpoch() === epoch ? id : undefined;
      })();
      inflight = work;
      // Clear tracking once this work settles (either outcome), but only if it's
      // still the active run — a reset()/new ensure() may have replaced it. This
      // is a separate chain (with both handlers) so it never leaves an unhandled
      // rejection; callers still get the original `work` (which rejects on
      // failure) to handle themselves.
      const clear = () => {
        if (inflight === work) inflight = null;
      };
      work.then(clear, clear);
      return work;
    },
  };
}
